import type { WrappedSourceId } from "@internal/backend";

/*
 * What each source's landing page says: the pitch, how the numbers get in,
 * the steps and the questions people actually search for. The video's own
 * words live in `sources.ts`.
 */

/** How a source's numbers reach the page. */
export type Intake =
  /**
   * A file the person's own AI makes from a prompt this page hands out: the year, already added up. Where the
   * platform also has an export of its own, that file is still taken when someone adds it instead. `fallback` is
   * the file input's filter for it. The page does not send anyone looking for one.
   */
  | { kind: "paste"; button: string; fallback?: { accept: string; multiple?: boolean } }
  /** A file the platform exports, read in the browser. `accept` is the file input's filter. */
  | {
      kind: "file";
      button: string;
      accept: string;
      /** The page on the platform where the export is made, linked next to the picker. */
      exportAt: ExportPage;
      multiple?: boolean;
      hideAmounts?: boolean;
    }
  /** A public name the server looks up. */
  | { kind: "lookup"; button: string; label: string; placeholder: string };

/** The 12 months a Wrapped covers, as the days an export should run between. */
export interface ExportYear {
  from: Date;
  to: Date;
}

/**
 * Where a platform hands out its export: the link's words and the page it opens. Where the platform takes the dates
 * in its address, the link opens with the last 12 months already set, so nobody has to find the range picker.
 */
export interface ExportPage {
  label: string;
  href: string | ((year: ExportYear) => string);
}

const DAY = 24 * 60 * 60 * 1000;
const unix = (date: Date) => Math.floor(date.getTime() / 1000);

/** The address an export link opens today: the same 365 days the video plays. */
export function exportHref(page: ExportPage, now: Date): string {
  if (typeof page.href === "string") return page.href;
  return page.href({ from: new Date(now.getTime() - 365 * DAY), to: now });
}

export interface SourcePage {
  /** The search result's title, without the site suffix. The year is added in season. */
  metaTitle: string;
  metaDescription: string;
  /** One or two short lines next to the title. */
  aside: string;
  /** The hub card's line. */
  card: string;
  intake: Intake;
  /** What is sent and what stays, in one line under the picker. Left out for lookups. */
  privacy?: string;
  steps: readonly [string, string, string];
  faq: readonly { question: string; answer: string }[];
  /** The closing section's title. */
  closing: string;
}

const ACCOUNT = "Free with a Hype Stack account.";

function moneyPage(name: string, exportStep: string, exportAt: ExportPage["href"]): SourcePage {
  return {
    metaTitle: `${name} Wrapped: Your Year in Sales as a Video`,
    metaDescription: `${name} Wrapped turns a payments export into a video you can post: sales, your best month, your biggest day and your founder type. Read in your browser, customer details never leave it.`,
    aside: `Your year on ${name} as a video you can post. Customer details never leave your browser. ${ACCOUNT}`,
    card: "Sales, your best month, your biggest day and your founder type. Revenue only if you want it shown.",
    intake: {
      kind: "file",
      button: "Pick your payments CSV",
      accept: ".csv,text/csv",
      exportAt: { label: `Get it in ${name}`, href: exportAt },
      hideAmounts: true,
    },
    privacy: "Read in your browser. Only daily sales counts, totals and product names are sent. Never a customer.",
    steps: [
      exportStep,
      "Pick the file here. Your browser reads it and counts the payments. Customer emails are counted and thrown away.",
      "Switch on Keep amounts private if you would rather post sales than revenue. Then your year plays.",
    ],
    faq: [
      {
        question: `What does ${name} Wrapped show?`,
        answer: `Your last 12 months of sales as a short video: how many you made, your best month, your biggest day, what sold, where your customers are, and a founder type picked from the shape of your year. You get a link and a card to post.`,
      },
      {
        question: "Is my revenue shown?",
        answer:
          "Only if you leave it on. With Keep amounts private switched on, the revenue never leaves your browser and the video counts sales instead.",
      },
      {
        question: "Are my customers' details uploaded?",
        answer:
          "No. The CSV is read by your browser on your machine. Emails are used to count distinct customers and are then dropped. What is sent is a count of sales per day, your totals, product names and countries.",
      },
      {
        question: "I sell in several currencies. What happens?",
        answer:
          "Revenue is added up in the currency most of your payments used. Sales in other currencies still count as sales, they are just left out of the revenue total, so the number is never a mix of dollars and euros.",
      },
    ],
    closing: "Your year in sales. Next year, a bigger one.",
  };
}

/**
 * The chat assistants are all made the same way: the assistant answers one prompt with a small file of counts.
 * Their pages differ in names and in a question or two.
 */
function chatPage({
  name,
  maker,
  file,
  fallback,
  exportAnswer,
  extraFaq,
  metaTitle,
}: {
  name: string;
  maker: string;
  /** The file the prompt asks for: "chatgpt-wrapped.json". */
  file: string;
  /** The platform's own export, taken when someone adds it instead of the prompt's file. */
  fallback: { accept: string; multiple?: boolean };
  /** How the platform's own export is used here, for the people who already have one. */
  exportAnswer: string;
  extraFaq: SourcePage["faq"];
  metaTitle: string;
}): SourcePage {
  return {
    metaTitle,
    metaDescription: `${name} Wrapped turns your year with ${name} into a video you can post: messages, your busiest day and your prompter type. One prompt, and ${name} counts it for you. Your chats never leave it.`,
    aside: `Your year with ${name} as a video you can post. ${name} counts it for you, and your chats stay where they are. ${ACCOUNT}`,
    card: `Messages, your busiest day, the hours you chat and your prompter type. ${name} makes the file from one prompt.`,
    intake: { kind: "paste", button: "Add your export", fallback },
    privacy: "The file holds a count per day and your totals. Never a message.",
    steps: [
      `Press Copy the prompt and paste it into a new chat in ${name}. It asks ${name} to count the messages you sent over the last 12 months and hand the result back as ${file}.`,
      "Add that file here: pick it, drop it or paste it. It holds a count per day and your totals. Open it first if you like, it is a few lines of numbers.",
      "Sign in, press Wrap it, and your year plays: messages, your busiest day, the hours you chat and your prompter type.",
    ],
    faq: [
      {
        question: `How do I get my ${name} Wrapped?`,
        answer: `Copy the prompt on this page and paste it into ${name}. It counts your messages and gives you a small file, ${file}. Add the file here, sign in, and your year plays as a video, with a link and a card you can post.`,
      },
      {
        question: "Are my conversations uploaded?",
        answer: `No. You never hand over a chat export. ${name} does the counting inside your own account, and the file it gives you holds numbers only: messages per day, your totals and the hours you chat. The prompt asks for exactly that in plain words, and you can open the file before you add it.`,
      },
      {
        question: "How accurate is it?",
        answer: `As accurate as what ${name} can see of your history. The prompt tells it to count and never to guess, and to leave out days it has no record of. If it can only reach back a few months, your video covers those months.`,
      },
      {
        question: `${name} says it cannot see my past chats. What now?`,
        answer: `Then it is not allowed to look at them. It is a switch in ${name}'s own settings. Turn it on, open a new chat and paste the prompt again.`,
      },
      { question: `Can I use my ${name} data export instead?`, answer: exportAnswer },
      ...extraFaq,
      {
        question: `Is this made by ${maker}?`,
        answer: `No. It is a free tool from Hype Stack. ${name} only writes the summary file, the way it would answer any other prompt. It works any time of year.`,
      },
    ],
    closing: "Your year in prompts. Next year, a product.",
  };
}

export const SOURCE_PAGES: Record<WrappedSourceId, SourcePage> = {
  "claude-code": {
    metaTitle: "Claude Code Wrapped: Check Your Claude Code Usage as a Video",
    metaDescription:
      "Check your Claude Code usage and play it as a video: prompts, token usage, sessions, your biggest day and your coder type. Read on your machine, and only the counts leave it.",
    aside: `Your Claude Code usage as a video you can post. Your transcripts never leave your machine. ${ACCOUNT}`,
    card: "Prompts, token usage, tool calls, your top projects and your coder type. Read from your own machine.",
    intake: { kind: "paste", button: "Add your export" },
    privacy: "Read on your machine. Only daily counts, totals, project names and model names are sent. Never a prompt.",
    steps: [
      "Press Copy the prompt and paste it into Claude Code. It carries a short script that adds up your session history on your machine and saves claude-code-wrapped.json in your Downloads.",
      "Add that file here: pick it, drop it or paste it. It holds a count per day, your totals, project names and model names. Never a prompt.",
      "Sign in, press Wrap it, and your year plays: prompts, token usage, your biggest day, your top projects and your coder type.",
    ],
    faq: [
      {
        question: "How do I check my Claude Code usage?",
        answer:
          "Claude Code keeps a transcript of every session in ~/.claude/projects. There is no export for it, so Claude Code makes one itself: you paste one prompt, it runs a short script that adds up prompts, sessions, tokens and tool calls per day and saves the result as a file. Add that file here and it plays as a video.",
      },
      {
        question: "Does it show my Claude Code token usage?",
        answer:
          "Yes. It adds up every token the model read and wrote, cache included, the same way Claude Code's own usage screens and ccusage count them, and shows the total, the models behind it and the day you used the most.",
      },
      {
        question: "Are my prompts or my code uploaded?",
        answer:
          "No. The script runs on your machine and sends nothing. The file it saves is a summary: a count per day, your totals, the names of your top projects and the models you used. Open it and look; it is a few kilobytes of numbers. Not one line of a prompt or a file is in it, and the script is in the prompt in full, so you can read it before it runs.",
      },
      {
        question: "Why does my year start so late?",
        answer:
          "Claude Code deletes old transcripts after a while (the cleanupPeriodDays setting, 30 days unless you changed it), so the folder usually holds weeks or months, not a full year. The video covers what is still on disk. Raise that setting and next year's will reach further back.",
      },
      {
        question: "How is this different from ccusage?",
        answer:
          "ccusage is a CLI that prints token usage and cost as tables. This reads the same local files and turns them into a video and a share card: your streak, your biggest day, your top projects, your coder type. It counts tokens the same way and leaves cost out.",
      },
    ],
    closing: "Your year in prompts. Next year, a product.",
  },
  codex: {
    metaTitle: "Codex Wrapped: Check Your Codex Usage as a Video",
    metaDescription:
      "Check your Codex usage and play it as a video: prompts, token usage, sessions, your biggest day and your coder type. Read on your machine, and only the counts leave it.",
    aside: `Your Codex usage as a video you can post. Your sessions never leave your machine. ${ACCOUNT}`,
    card: "Prompts, token usage, tool calls, your top projects and your coder type. Read from your own machine.",
    intake: { kind: "paste", button: "Add your export" },
    privacy: "Read on your machine. Only daily counts, totals, project names and model names are sent. Never a prompt.",
    steps: [
      "Press Copy the prompt and paste it into Codex. It carries a short script that adds up your session history on your machine and saves codex-wrapped.json in your Downloads.",
      "Add that file here: pick it, drop it or paste it. It holds a count per day, your totals, project names and model names. Never a prompt.",
      "Sign in, press Wrap it, and your year plays: prompts, token usage, your biggest day, your top projects and your coder type.",
    ],
    faq: [
      {
        question: "How do I check my Codex usage?",
        answer:
          "Codex keeps a file for every session in ~/.codex/sessions, whether you used the app, the CLI or the editor extension. There is no export for it, so Codex makes one itself: you paste one prompt, it runs a short script that adds up prompts, sessions, tokens and tool calls per day and saves the result as a file. Add that file here and it plays as a video.",
      },
      {
        question: "Does it show my Codex token usage?",
        answer:
          "Yes. It adds up what every response read and wrote, cached input included, and shows the total, the models behind it and the day you used the most. The threads Codex opens on its own, to review an approval or run a sub-agent, count for their tokens and not as sessions of yours.",
      },
      {
        question: "Are my prompts or my code uploaded?",
        answer:
          "No. The script runs on your machine and sends nothing. The file it saves is a summary: a count per day, your totals, the names of your top projects and the models you used. Open it and look; it is a few kilobytes of numbers. Not one line of a prompt or a file is in it, and the script is in the prompt in full, so you can read it before it runs.",
      },
      {
        question: "Is this the same as ChatGPT Wrapped?",
        answer:
          "No. ChatGPT Wrapped counts your chats, and ChatGPT adds those up itself. This one counts the coding you did with Codex, from the sessions on your own machine. Both are on the same page, behind the Chats and Codex tabs.",
      },
    ],
    closing: "Your year in prompts. Next year, a product.",
  },
  claude: chatPage({
    name: "Claude",
    maker: "Anthropic",
    metaTitle: "Claude Wrapped: Your Year with Claude as a Video",
    file: "claude-wrapped.json",
    fallback: { accept: ".zip,application/zip", multiple: true },
    exportAnswer:
      "Yes. If you already asked Claude for an export (Settings, then Privacy, then Export data), add the zip called conversations to the field, or the conversations.json inside it. Your browser reads it on your machine and only the counts are sent. It counts every message, where the prompt counts what Claude can see.",
    extraFaq: [
      {
        question: "Is this for Claude Code too?",
        answer:
          "No, this one counts your chats on claude.ai. Claude Code keeps its history on your own machine, and Claude Code Wrapped counts that. Both are on the same page, behind the Claude Code and Chats tabs.",
      },
    ],
  }),
  cursor: {
    metaTitle: "Cursor Wrapped: Check Your Cursor Usage as a Video",
    metaDescription:
      "Check your Cursor usage and play it as a video: requests, the models you leaned on, lines written, your biggest day and your coder type. Read on your machine, and only the counts leave it.",
    aside: `Your Cursor usage as a video you can post. Your chats never leave your machine. ${ACCOUNT}`,
    card: "Requests, the models you leaned on, lines written and your coder type. Read from your own machine.",
    intake: { kind: "paste", button: "Add your export", fallback: { accept: ".csv,text/csv" } },
    privacy: "Read on your machine. Only daily counts, totals, project names and model names are sent. Never a prompt.",
    steps: [
      "Press Copy the prompt and paste it into Cursor's agent. It carries a short script that adds up the chat history Cursor keeps on your machine and saves cursor-wrapped.json in your Downloads.",
      "Add that file here: pick it, drop it or paste it. It holds a count per day, your totals, project names and model names. Never a prompt.",
      "Sign in, press Wrap it, and your year plays: requests, the models you used most, lines written, your top projects and your coder type.",
    ],
    faq: [
      {
        question: "How do I check my Cursor usage?",
        answer:
          "Cursor keeps every chat in a database in its settings folder on your machine. There is no export for it, so Cursor makes one itself: you paste one prompt into its agent, it runs a short script that adds up your requests per day, the models behind them and the lines the agent wrote, and saves the result as a file. Add that file here and it plays as a video.",
      },
      {
        question: "What does Cursor Wrapped show?",
        answer:
          "How many requests you sent, which models you leaned on, how many lines the agent wrote, your top projects, the day you used it most, your longest streak and a coder type picked from your habits.",
      },
      {
        question: "Is my code uploaded?",
        answer:
          "No. The script runs on your machine and sends nothing. It opens Cursor's history read-only, and the file it saves is a summary: a count per day, your totals, the names of your top projects and the models you used. Not one line of a prompt or a file is in it, and the script is in the prompt in full, so you can read it before it runs.",
      },
      {
        question: "Does it show my Cursor token usage?",
        answer:
          "Not from the prompt: Cursor keeps token counts on its own dashboard, not on your machine. If you want them in the video, export the usage CSV from the dashboard's Usage page and add that file to the field instead. It is read by your browser, and it brings your tokens with it.",
      },
      {
        question: "What counts as a request?",
        answer:
          "A message you sent in Cursor's chat or to its agent. Tab completions are not kept in the history, so they are not counted, and neither are the messages a sub-agent was sent on your behalf.",
      },
    ],
    closing: "Your year in Cursor. Next year, a product.",
  },
  chatgpt: chatPage({
    name: "ChatGPT",
    maker: "OpenAI",
    metaTitle: "ChatGPT Wrapped: How to Get Your Year with ChatGPT as a Video",
    file: "chatgpt-wrapped.json",
    fallback: { accept: ".zip,application/zip" },
    exportAnswer:
      "Yes. If you already have the zip ChatGPT emails you (Settings, then Data controls, then Export data), add it to the field, or the conversations.json inside it. Your browser reads it on your machine and only the counts are sent. It counts every message, where the prompt counts what ChatGPT can see.",
    extraFaq: [
      {
        question: "Is this the same as Your Year with ChatGPT?",
        answer:
          "No. Your Year with ChatGPT is OpenAI's own recap, shown in the app at the end of the year. This works any month, and gives you a video and a card to post.",
      },
      {
        question: "Is there a ChatGPT Wrapped prompt?",
        answer:
          "Yes, and it is the one on this page. Most prompts going around ask ChatGPT to guess your year from what it remembers about you. This one asks it to count what it can see and write the numbers into a file, and the file becomes a video.",
      },
      {
        question: "What counts as a message?",
        answer:
          "Every message you sent in the last 12 months that ChatGPT can still see. Deleted chats and temporary chats are gone, so they are not counted.",
      },
    ],
  }),
  gemini: chatPage({
    name: "Gemini",
    maker: "Google",
    metaTitle: "Gemini Wrapped: Your Gemini Usage as a Video",
    file: "gemini-wrapped.json",
    fallback: { accept: ".zip,.html,application/zip,text/html" },
    exportAnswer:
      "Yes. If you already have a Google Takeout export of My Activity, add the zip to the field as it comes. Your browser finds the Gemini part and reads it on your machine, and only the counts are sent. It counts every prompt, where the prompt counts what Gemini can see.",
    extraFaq: [
      {
        question: "Does it cover the Gemini API or the CLI?",
        answer:
          "No. It covers your chats in the Gemini app (gemini.google.com and the mobile apps), which is what Gemini itself can see.",
      },
    ],
  }),
  stripe: moneyPage(
    "Stripe",
    "Sign in here. Then in the Stripe dashboard, open Payments, press Export, choose the last 12 months and download the CSV.",
    // Stripe keeps a list's filters in its address: paid payments, created inside the year.
    ({ from, to }) =>
      `https://dashboard.stripe.com/payments?status[0]=successful&created[gte]=${unix(from)}&created[lte]=${unix(to)}`,
  ),
  "lemon-squeezy": moneyPage(
    "Lemon Squeezy",
    "Sign in here. Then in your Lemon Squeezy dashboard, open Orders and export them as a CSV.",
    "https://app.lemonsqueezy.com/orders",
  ),
  polar: moneyPage(
    "Polar",
    "Sign in here. Then in your Polar dashboard, open Sales, then Orders, and export them as a CSV.",
    "https://polar.sh/dashboard",
  ),
  "search-console": {
    metaTitle: "Search Console Wrapped: Your Google Search Console Performance Report as a Video",
    metaDescription:
      "Search Console Wrapped turns your Google Search Console performance report into a video you can post: clicks, impressions, top queries, your best month and your biggest day. Read in your browser.",
    aside: `Your site's year in Google Search as a video you can post. The export is read in your browser. ${ACCOUNT}`,
    card: "Clicks, impressions, top queries, your best month and your biggest day. From the Performance export.",
    intake: {
      kind: "file",
      button: "Pick your Performance export",
      accept: ".zip,.csv,application/zip,text/csv",
      exportAt: {
        label: "Get it in Search Console",
        href: "https://search.google.com/search-console/performance/search-analytics?num_of_months=12",
      },
      multiple: true,
    },
    privacy: "Read in your browser. Only daily clicks, totals, top queries and countries are sent.",
    steps: [
      "Sign in here. Then in Search Console, open Performance, set the date range to the last 12 months, press Export and choose Download CSV. It arrives as a zip.",
      "Pick that zip here, as it came. Your browser reads Dates.csv, Queries.csv and Countries.csv out of it.",
      "Your year plays. Switch the top queries scene off first if you would rather keep them to yourself.",
    ],
    faq: [
      {
        question: "What does Search Console Wrapped show?",
        answer:
          "Your Google Search Console performance report for the last 12 months as a short video: clicks, impressions, click-through rate and average position, your best month, your biggest day, your top queries and the countries that searched for you.",
      },
      {
        question: "How do I export the performance report from Search Console?",
        answer:
          "Open the Performance report, set the date range to the last 12 months and press Export in the top right. The menu offers Google Sheets, Excel and CSV: choose Download CSV. There is no zip option because the CSV download is the zip: one file with a small CSV per table inside, and that file is what this page reads.",
      },
      {
        question: "Are my queries uploaded?",
        answer:
          "The zip is read by your browser. Your top eight queries are sent, because they appear in the video. If you would rather not show them, switch that scene off before sharing the link.",
      },
      {
        question: "Why only 1,000 queries?",
        answer:
          "Search Console's own export stops at 1,000 rows per table. The clicks and impressions totals come from the per-day file, so they are complete either way.",
      },
    ],
    closing: "Your year in search. Next year, more of it.",
  },
  x: {
    metaTitle: "X Wrapped: Your Year on X (Twitter) as a Video",
    metaDescription:
      "X Wrapped, the Twitter Wrapped that counts your real archive: posts, likes, your best post, your busiest day and your poster type, as a video you can post. Read in your browser.",
    aside: `Your year on X as a video you can post. Your archive is read in your browser. ${ACCOUNT}`,
    card: "Posts, replies, likes, your best post and your poster type. From your X archive.",
    intake: {
      kind: "file",
      button: "Pick your X archive",
      accept: ".zip,.js,application/zip",
      exportAt: { label: "Get it on X", href: "https://x.com/settings/download_your_data" },
    },
    privacy: "Read in your browser. Only daily counts, totals and your five most liked posts are sent. Never a DM.",
    steps: [
      "Sign in here. Then on X, open Settings, then Your account, and press Download an archive of your data. It takes about a day to arrive.",
      "Pick the zip here. Your browser opens one file in it, data/tweets.js, and nothing else.",
      "Your year plays: posts, replies, likes, your best post, your busiest day and your poster type.",
    ],
    faq: [
      {
        question: "Is this Twitter Wrapped?",
        answer:
          "Yes. X and Twitter are the same place, and this is a Wrapped for it: your last 12 months of posts counted from your own archive and played as a video.",
      },
      {
        question: "How do I get my X archive?",
        answer:
          "On X, open Settings and privacy, then Your account, then Download an archive of your data. X asks for your password, prepares the archive and emails you when it is ready, usually within a day.",
      },
      {
        question: "Are my DMs or anything private read?",
        answer:
          "No. The archive holds a lot, including direct messages, and this page opens exactly one file in it: data/tweets.js, your public posts. It is read by your browser. What is sent is a count per day, your totals and the text of your five most liked posts, which are public already.",
      },
      {
        question: "Why not just type a username?",
        answer:
          "X no longer has a free public API, so there is no honest way to read someone's year from outside. Your own archive is the complete record, and it is yours.",
      },
    ],
    closing: "A year of posts. Next year, a launch thread.",
  },
  npm: {
    metaTitle: "npm Wrapped: npm Package Stats and Downloads for a Year, as a Video",
    metaDescription:
      "npm Wrapped turns any public package's npm stats into a video you can post: a year of downloads, the best month, the biggest day. Works for one package or everything a maintainer ships.",
    aside: `A package's year on npm as a video you can post. Works for any public package. ${ACCOUNT}`,
    card: "Downloads, the best month and the biggest day, for any public package or everything one maintainer ships.",
    intake: {
      kind: "lookup",
      button: "Wrap it",
      label: "Package, or ~maintainer",
      placeholder: "react-zoom-pan-pinch",
    },
    steps: [
      "Pick One package or All my packages, then type the package name or your npm username.",
      "A year somebody already made plays right away. A new one takes a sign-in, then we read npm's public download counts for the last 365 days.",
      "The year plays. Copy the link and anyone can watch it.",
    ],
    faq: [
      {
        question: "Where can I see npm package stats?",
        answer:
          "npm publishes download statistics for every public package through its downloads API, the same numbers the package page on npmjs.com shows. npm Wrapped reads the last 365 days of them, one count per day, and plays the year: total downloads, the best month, the biggest day and how the second half compares with the first.",
      },
      {
        question: "Can I wrap a scoped package?",
        answer: "Yes. Type it the way you install it, for example @hyper-fetch/core.",
      },
      {
        question: "Can I wrap everything I maintain?",
        answer:
          "Yes. Pick All my packages and type your npm username. Downloads are added up across your packages and the video ranks the top ones.",
      },
      {
        question: "Why do I need to sign in?",
        answer:
          "Watching is open to everyone. Making a new one makes our server go and read npm, and a sign-in is what keeps bots from making it read a million package names.",
      },
      {
        question: "Why does the year end yesterday?",
        answer: "npm publishes a day's download count the day after, so the newest complete day is always yesterday.",
      },
    ],
    closing: "A year of downloads. Next year, a product on top.",
  },
  repo: {
    metaTitle: "Repo Wrapped: GitHub Star History and Repo Stats as a Video",
    metaDescription:
      "Repo Wrapped turns a public GitHub repository's year into a video you can post: commits, star history, releases, top contributors and languages. Type owner/name.",
    aside: `A repository's year on GitHub as a video you can post. Any public repo. ${ACCOUNT}`,
    card: "Commits, star history, releases, top contributors and languages, for any public repository.",
    intake: {
      kind: "lookup",
      button: "Wrap it",
      label: "Repository, as owner/name",
      placeholder: "BetterTyped/hype-stack",
    },
    steps: [
      "Type a public repository as owner/name, or paste its GitHub link.",
      "A year somebody already made plays right away. A new one takes a sign-in with GitHub, because GitHub only hands out a repository's history to someone signed in.",
      "The year plays: commits, new stars, releases, who built it and what it is written in.",
    ],
    faq: [
      {
        question: "How do I see a GitHub repo's star history?",
        answer:
          "GitHub records when every star was given. Repo Wrapped reads the stars of the last 12 months for the repository you type and shows how many it gained, next to its commits, releases and contributors for the same year.",
      },
      {
        question: "What repo stats does it show?",
        answer:
          "Commits per day for the last year, the biggest day, the longest streak, stars gained and stars in total, forks, releases shipped, the people who committed most, and the languages the code is written in.",
      },
      {
        question: "Why do I have to sign in with GitHub?",
        answer:
          "GitHub's API gives each signed-in person their own allowance of requests and next to nothing to anonymous callers. Signing in with GitHub lets the page read the repository on your allowance. It asks for your public profile and email, and cannot see or change your code.",
      },
      {
        question: "Does it work for private repositories?",
        answer: "No. It only reads public repositories.",
      },
      {
        question: "The star count for the year looks low on a huge repo. Why?",
        answer:
          "GitHub stops paging through stars after a point, so on repositories with tens of thousands of stars the newest ones cannot be listed. The total is still right; the number gained this year may be missing.",
      },
    ],
    closing: "A year of commits. Next year, your own repo.",
  },
  "hacker-news": {
    metaTitle: "Hacker News Wrapped: Your Year on HN as a Video",
    metaDescription:
      "Hacker News Wrapped turns your last 12 months on HN into a video you can post: stories, comments, your best thread, your busiest day and your poster type.",
    aside: `Your year on Hacker News as a video you can post. Public data only. ${ACCOUNT}`,
    card: "Stories, comments, your best thread and your poster type. From your public profile.",
    intake: { kind: "lookup", button: "Wrap it", label: "Hacker News username", placeholder: "pg" },
    steps: [
      "Type a Hacker News username. Capital letters matter.",
      "A year somebody already made plays right away. A new one takes a sign-in, then we read the public stories and comments from the last 365 days.",
      "The year plays. Copy the link and anyone can watch it.",
    ],
    faq: [
      {
        question: "Where do the numbers come from?",
        answer:
          "From Hacker News's public API and the public Algolia search index of the site. Nothing here needs your Hacker News password.",
      },
      {
        question: "Why are there no points for my comments?",
        answer:
          "Hacker News does not publish comment scores to anyone but their author. The points in the video are the points on your stories.",
      },
      {
        question: "Is the karma for this year?",
        answer: "No. Karma is your all-time number as of today. Hacker News does not publish karma per year.",
      },
    ],
    closing: "A year of threads. Next year, a Show HN.",
  },
  bluesky: {
    metaTitle: "Bluesky Wrapped: Your Year on Bluesky as a Video",
    metaDescription:
      "Bluesky Wrapped turns your last 12 months on Bluesky into a video you can post: posts, replies, likes, your best post, your busiest day and your poster type. Type a handle.",
    aside: `Your year on Bluesky as a video you can post. Public posts only. ${ACCOUNT}`,
    card: "Posts, replies, likes, your best post and your poster type. From your public profile.",
    intake: { kind: "lookup", button: "Wrap it", label: "Bluesky handle", placeholder: "you.bsky.social" },
    steps: [
      "Type a Bluesky handle, like you.bsky.social. A custom domain works too.",
      "A year somebody already made plays right away. A new one takes a sign-in, then we read the public posts from the last 365 days.",
      "The year plays: posts, replies, likes, your best post and your poster type.",
    ],
    faq: [
      {
        question: "How do I get my Bluesky Wrapped?",
        answer:
          "Sign in, type your handle and press the button. Your last 12 months of public posts are read from Bluesky's open API and played as a video, with a link and a card you can post.",
      },
      {
        question: "What Bluesky account stats does it show?",
        answer:
          "How many posts and replies you wrote, on which days, the likes and reposts they collected, your most liked posts, your longest streak, your followers today, and a poster type picked from your habits.",
      },
      {
        question: "Do I have to give my Bluesky password?",
        answer:
          "No. Bluesky is an open network and public posts can be read by anyone. Nothing here asks for your Bluesky login.",
      },
      {
        question: "Does it count reposts?",
        answer:
          "Reposts you made are counted on their own and shown in the split. Your days and your likes only count posts you wrote.",
      },
    ],
    closing: "A year of posts. Next year, a launch thread.",
  },
  reddit: {
    metaTitle: "Reddit Wrapped: Your Reddit Recap as a Video",
    metaDescription:
      "Reddit Wrapped is a Reddit recap you can get any month: your posts and comments for the last 12 months, your top subreddits, your best post and your redditor type, as a video. Type a username.",
    aside: `Your year on Reddit as a video you can post. Public posts and comments only. ${ACCOUNT}`,
    card: "Posts, comments, your top subreddits, your best post and your redditor type. From your public profile.",
    intake: { kind: "lookup", button: "Wrap it", label: "Reddit username", placeholder: "spez" },
    steps: [
      "Type a Reddit username, with or without the u/.",
      "A year somebody already made plays right away. A new one takes a sign-in, then we read the profile's public posts and comments from the last 365 days.",
      "The year plays: your top subreddits, your best post, your busiest day and your redditor type.",
    ],
    faq: [
      {
        question: "How do I see my Reddit Wrapped?",
        answer:
          "Sign in, type your username and press the button. Your public posts and comments from the last 12 months are read through Reddit's API and played as a video, with a link and a card you can post.",
      },
      {
        question: "Is this Reddit Recap?",
        answer:
          "No. Reddit Recap is Reddit's own year-end summary, shown in the app in December. This is a separate, free tool that works any month, on any public profile, and gives you a video.",
      },
      {
        question: "Do I have to give my Reddit password?",
        answer: "No. It only reads what is public on the profile. Nothing here asks for your Reddit login.",
      },
      {
        question: "Why does a very active account look capped?",
        answer:
          "Reddit lists about the last thousand posts and the last thousand comments of a profile and nothing older. For most people that covers the year; for the most active it covers the most recent months.",
      },
      {
        question: "Is the karma for this year?",
        answer: "No. Karma is the profile's all-time number as of today.",
      },
    ],
    closing: "A year of threads. Next year, something to post about.",
  },
};

/**
 * Sources that are built but not offered: no hub card, no switcher entry, no page, nothing in the sitemap.
 * Reddit reads profiles as an app, and Reddit is not handing out new apps (2026-10-10). Take an id out of
 * this set and it is back everywhere.
 */
export const HIDDEN_SOURCES: ReadonlySet<string> = new Set<WrappedSourceId>(["reddit"]);

/** The landing pages on offer, as [source, page]. Everything that lists Wrappeds reads this, never `SOURCE_PAGES`. */
export const OFFERED_PAGES = (Object.entries(SOURCE_PAGES) as [WrappedSourceId, SourcePage][]).filter(
  ([source]) => !HIDDEN_SOURCES.has(source),
);

/**
 * The year a Wrapped is searched with, in season: people type "github wrapped 2026" from November until the
 * following January. Outside the season there is no year to add.
 */
export function wrappedSeasonYear(now = new Date()): number | null {
  const month = now.getMonth();
  if (month >= 10) return now.getFullYear();
  if (month === 0) return now.getFullYear() - 1;
  return null;
}

/** A Wrapped's search title with the season's year worked in: "ChatGPT Wrapped 2026: Your Year with ChatGPT as a Video". */
export function seasonalTitle(title: string, now = new Date()): string {
  const year = wrappedSeasonYear(now);
  return year ? title.replace(/Wrapped(?!\s\d{4})/, `Wrapped ${year}`) : title;
}
